/* eslint-disable @typescript-eslint/no-magic-numbers */
import { ScreenSize } from '@libs/shared-services';
import {
  CoordinateRecord,
  GUM_PADDING_X,
  PERIO_QUADRANT_DEFAULT_TEETH_PADDING_X,
  PERIO_TEETH_CONTAINER_PADDING_X,
  Tooth,
  TOOTH_WIDTH,
  ToothCoordinates,
  translateY,
} from '@libs/ui-lib';
import { getMeasurementsCoordinates } from '../teeth/coordinates';

export const calculateCoordinates = (
  teeth: Tooth[],
  screenSize: ScreenSize,
  teethTranslateY?: { upper: { buccal: number; lingual: number }; lower: { buccal: number; lingual: number } },
): ToothCoordinates => {
  // 1. separate teeth by jaw
  const upperJawTeeth = teeth.filter((tooth) => tooth.jaw === 'upper');
  const lowerJawTeeth = teeth.filter((tooth) => tooth.jaw === 'lower');
  // 2. get all teeth coordinates
  const upperJawTeethCoordinates = getTeethCoordinates(upperJawTeeth, screenSize);
  const upperJawBuccalTeethCoordinates = translateY(upperJawTeethCoordinates, teethTranslateY?.upper.buccal ?? 0);

  const upperJawLingualTeethCoordinates = translateY(upperJawTeethCoordinates, teethTranslateY?.upper.lingual ?? 0);

  const lowerJawTeethCoordinates = getTeethCoordinates(lowerJawTeeth, screenSize);
  const lowerJawLingualTeethCoordinates = translateY(lowerJawTeethCoordinates, teethTranslateY?.lower.buccal ?? 0);
  const lowerJawBuccalTeethCoordinates = translateY(lowerJawTeethCoordinates, teethTranslateY?.lower.lingual ?? 0);

  // 3. based on teeth coordinates, get perios coordinates
  const upperJawBuccalPeriosCoordinates = getMeasurementsCoordinates(upperJawTeeth, upperJawBuccalTeethCoordinates, 'buccal', 'lower');

  const upperJawLingualPeriosCoordinates = getMeasurementsCoordinates(upperJawTeeth, upperJawLingualTeethCoordinates, 'lingual', 'lower');

  const lowerJawLingualPeriosCoordinates = getMeasurementsCoordinates(lowerJawTeeth, lowerJawLingualTeethCoordinates, 'lingual', 'upper');
  const lowerJawBuccalPeriosCoordinates = getMeasurementsCoordinates(lowerJawTeeth, lowerJawBuccalTeethCoordinates, 'buccal', 'upper');
  return {
    upperJawTeeth,
    lowerJawTeeth,

    upperJawBuccalTeethCoordinates,
    upperJawLingualTeethCoordinates,
    lowerJawLingualTeethCoordinates,
    lowerJawBuccalTeethCoordinates,

    upperJawBuccalPeriosCoordinates,
    upperJawLingualPeriosCoordinates,
    lowerJawLingualPeriosCoordinates,
    lowerJawBuccalPeriosCoordinates,
  };
};

/**
 * Calculates the coordinates of all teeth based on their position and jaw. This function can handle any jaw configuration,
 * but is designed to display every jaw and quadrant in a unified way. Spacing can be set manually,
 * @param screenSize
 * @param teethSpacing
 */
const addToothCoordinatesToRecord =
  (screenSize: ScreenSize, teethSpacing = PERIO_QUADRANT_DEFAULT_TEETH_PADDING_X) =>
  (acc: CoordinateRecord, { position }: Tooth, i: number): CoordinateRecord => {
    return {
      ...acc,
      [position]: {
        x: PERIO_TEETH_CONTAINER_PADDING_X(screenSize) + GUM_PADDING_X + i * (TOOTH_WIDTH(screenSize) + teethSpacing),
        y: 0,
      },
    };
  };

export const getTeethCoordinates = (
  teeth: Tooth[],
  screenSize: ScreenSize,
  teethSpacing = PERIO_QUADRANT_DEFAULT_TEETH_PADDING_X,
): CoordinateRecord => {
  return teeth.reduce(addToothCoordinatesToRecord(screenSize, teethSpacing), {} as CoordinateRecord);
};
